import React from "react"
import { Flex, PseudoBox, useDisclosure, Icon } from "@chakra-ui/core"

import SmallCaps from "../typography/SmallCaps"
import BottomDrawer from "../BottomDrawer"
import GridSelectOption from "../GridSelectOption"
import { LaptopDownOnly, LaptopUpOnly } from "../../utils/MediaQueries"

export interface OptionType {
  label: string
  shortLabel?: string
  name: string
  isDisabled?: boolean
  subText?: string
}

export interface GenericBoxSelectorProps {
  options: Array<OptionType>
  onSelect: (name: string) => void
  selectedIndex: number
}

const BoxSelectorMobile: React.FC<
  GenericBoxSelectorProps & {
    title: string
    mobileView?: "square" | "warranty"
  }
> = ({ title, options, selectedIndex, onSelect, mobileView = "square" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const isWarranty = mobileView === "warranty"
  const selectedOption = options?.[selectedIndex]
  return (
    <>
      <Flex onClick={onOpen} alignItems="center">
        <SmallCaps m="0 1rem" color="dusk" fontWeight="bold">
          {selectedOption?.shortLabel || selectedOption?.label}
        </SmallCaps>
        <Icon
          name="chevron"
          size="1.25rem"
          transform="rotate(-90deg)"
          color="dawn"
        />
      </Flex>
      <BottomDrawer heading={title} isOpen={isOpen} onClose={onClose}>
        <Flex
          align="stretch"
          pt="2.1875rem"
          pb="2.5rem"
          px="1.875rem"
          whiteSpace="nowrap"
          overflowX="auto"
        >
          {options.map((option, index) => (
            <GridSelectOption
              key={`${title}-option-block-${option.name}`}
              active={index === selectedIndex}
              flexGrow={isWarranty ? 1 : 0}
              itemSize={isWarranty ? "wide" : "small"}
              option={
                isWarranty
                  ? {
                      value: option.name,
                      name:
                        index === selectedIndex
                          ? "Included"
                          : option.subText || "",
                      innerText: option.label,
                    }
                  : {
                      value: option.name,
                      name: option.label || "",
                      innerText: option.shortLabel,
                      price: option.subText,
                    }
              }
              onClick={
                !option.isDisabled
                  ? () => {
                      onSelect(option.name)
                      onClose()
                    }
                  : undefined
              }
              ml={index === 0 ? "0" : "0.25rem"}
              isDisabled={option.isDisabled}
            />
          ))}
        </Flex>
      </BottomDrawer>
    </>
  )
}
const BoxSelectorDesktop: React.FC<GenericBoxSelectorProps> = ({
  options,
  onSelect,
  selectedIndex,
}) => {
  return (
    <Flex flexWrap="wrap" align="center" justify="flex-end">
      {options?.map((option, index) => (
        <PseudoBox
          key={option.label}
          p="0.625rem 0.875rem"
          borderRadius="0.5rem"
          mr={index !== options.length - 1 ? "0.25rem" : 0}
          border="1px solid"
          borderColor={index === selectedIndex ? "night" : "transparent"}
          _hover={
            option.isDisabled
              ? { cursor: "not-allowed" }
              : {
                  borderColor: index === selectedIndex ? "night" : "dawn",
                  cursor: "pointer",
                }
          }
          onClick={
            !option.isDisabled
              ? () => {
                  onSelect(option.name)
                }
              : undefined
          }
        >
          <SmallCaps
            size="lg"
            fontWeight="bold"
            color={selectedIndex === index ? "night" : "dusk"}
            opacity={option.isDisabled ? 0.5 : 1}
            whiteSpace="nowrap"
          >
            {option.shortLabel || option.label}
          </SmallCaps>
        </PseudoBox>
      ))}
    </Flex>
  )
}

export interface BoxSelectorProps {
  options: Array<OptionType>
  onSelect: (name: string) => void
  defaultSelectedIndex?: number
  title?: string
  mobileView?: "square" | "warranty"
}

const BoxSelector: React.FC<BoxSelectorProps> = (props) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number>(
    props.defaultSelectedIndex ?? 0
  )

  React.useEffect(() => {
    if (props.defaultSelectedIndex !== undefined) {
      setSelectedIndex(props.defaultSelectedIndex)
    }
  }, [props.defaultSelectedIndex])

  return (
    <>
      <LaptopDownOnly>
        <BoxSelectorMobile
          options={props.options}
          onSelect={(name: string) => {
            props.onSelect(name)
            setSelectedIndex(
              props.options.findIndex((option) => option.name === name)
            )
          }}
          selectedIndex={selectedIndex}
          title={props.title ?? ""}
          mobileView={props.mobileView}
        />
      </LaptopDownOnly>
      <LaptopUpOnly>
        <BoxSelectorDesktop
          options={props.options}
          onSelect={(name: string) => {
            props.onSelect(name)
            setSelectedIndex(
              props.options.findIndex((option) => option.name === name)
            )
          }}
          selectedIndex={selectedIndex}
        />
      </LaptopUpOnly>
    </>
  )
}

export default BoxSelector
